/**
 * Get initials from first and last name.
 */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * Deterministic color from a string (userId). Returns a hex color
 * that works well as a background with white text.
 */
const AVATAR_COLORS = [
  '#0e4d8f', // Deep Navy
  '#1565c0', // Royal Blue
  '#4f6d7a', // Warm Beige (teal-gray)
  '#0b345f', // Steel Blue
  '#6e7a8a', // Cool Mint
  '#f31a25', // Red
  '#2f3a45', // Slate Gray
  '#7b1fa2', // Purple
  '#00838f', // Teal
  '#558b2f', // Green
];

export function getUserColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/**
 * Read a File as a base64 data URI, resized to maxSize x maxSize.
 * Returns a Promise that resolves to the data URI string.
 */
export function readFileAsResizedBase64(
  file: File,
  maxSize = 150
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
